function parseDateSafe(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfUtcDay(value) {
  const date = value instanceof Date ? value : parseDateSafe(value);
  if (!date) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addUtcDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function getYesterdayUtcDay() {
  return addUtcDays(startOfUtcDay(new Date()), -1);
}

function getDateKey(date) {
  return date.toISOString().slice(0, 10);
}

function toUtcStartOfDayIso(value) {
  const date = startOfUtcDay(value);
  return date ? date.toISOString() : null;
}

export function parseRevenueDate(value) {
  return parseDateSafe(value);
}

export function formatCurrencyCompact(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  if (abs >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (abs >= 1000) return `$${(amount / 1000).toFixed(2)}K`;
  return `$${amount.toFixed(2)}`;
}

export function formatChangePercent(current, previous) {
  if (previous <= 0) return current > 0 ? "100.0%" : "0.0%";
  return `${(((current - previous) / previous) * 100).toFixed(1)}%`;
}

export function normalizeBookingRecord(row) {
  const bookedAt = parseDateSafe(row?.fh_booked_at) || parseDateSafe(row?.fh_start_at);
  const revenue = Number(row?.fh_total_revenue || 0);
  const status = String(row?.fh_status || "").toLowerCase();
  const excluded = status.includes("cancel") || status.includes("refund") || status.includes("void");

  return {
    ...row,
    bookedAt,
    revenue,
    included: !excluded && revenue > 0 && Boolean(bookedAt),
  };
}

function getRangeDates(startDate, endDate) {
  const requestedEnd = startOfUtcDay(endDate) || startOfUtcDay(new Date());
  const yesterday = getYesterdayUtcDay();
  const end = requestedEnd > yesterday ? yesterday : requestedEnd;
  const start = startOfUtcDay(startDate) || end;
  return start > end ? { start: end, end } : { start, end };
}

export function getComparisonRange(startDate, endDate, period = "last 30 days") {
  const { start, end } = getRangeDates(startDate, endDate);

  if (period === "month to date") {
    const elapsedDays = Math.max(0, Math.round((end - start) / 86400000));
    const previousMonthStart = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 1, 1));
    const previousMonthEnd = addUtcDays(previousMonthStart, elapsedDays);
    const monthEnd = new Date(Date.UTC(previousMonthStart.getUTCFullYear(), previousMonthStart.getUTCMonth() + 1, 0));

    return {
      start: getDateKey(previousMonthStart),
      end: getDateKey(previousMonthEnd > monthEnd ? monthEnd : previousMonthEnd),
      label: "same period last month",
    };
  }

  if (period === "year to date") {
    const elapsedDays = Math.max(0, Math.round((end - start) / 86400000));
    const previousYearStart = new Date(Date.UTC(start.getUTCFullYear() - 1, 0, 1));
    const previousYearEnd = addUtcDays(previousYearStart, elapsedDays);
    return {
      start: getDateKey(previousYearStart),
      end: getDateKey(previousYearEnd),
      label: "same period last year",
    };
  }

  const totalDays = Math.max(1, Math.round((end - start) / 86400000) + 1);
  const comparisonEnd = addUtcDays(start, -1);
  const comparisonStart = addUtcDays(comparisonEnd, -(totalDays - 1));

  return {
    start: getDateKey(comparisonStart),
    end: getDateKey(comparisonEnd),
    label: "previous period",
  };
}

export function buildBookedRevenueBookingFilters(startDate, endDate, period = "last 30 days") {
  const currentRange = getRangeDates(startDate, endDate);
  const comparisonRange = getComparisonRange(currentRange.start, currentRange.end, period);
  const exclusiveEnd = addUtcDays(currentRange.end, 1);

  // In dev we always have null subject_id
  const devHosts = new Set([
    'publisher.com',
    'dev.openpath.ai',
    'preview.openpath.ai'
  ]);

  return [
    {
      fh_booked_at: {
        gte: toUtcStartOfDayIso(comparisonRange.start),
        lt: toUtcStartOfDayIso(exclusiveEnd)
      },
      // This condition goes in except in our dev boxes
      ...(!devHosts.has(window.location.hostname) ? { subject_id: { ne: null } } : undefined)
    }
  ];
}

export function buildDailyRevenueSeries(bookings, startDate, endDate) {
  const { start, end } = getRangeDates(startDate, endDate);
  const totals = new Map();

  bookings.forEach((booking) => {
    const key = getDateKey(startOfUtcDay(booking.bookedAt));
    totals.set(key, (totals.get(key) || 0) + booking.revenue);
  });

  const points = [];
  for (let cursor = new Date(start); cursor <= end; cursor = addUtcDays(cursor, 1)) {
    const key = getDateKey(cursor);
    points.push({
      date: key,
      label: cursor.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
      value: totals.get(key) || 0,
    });
  }

  return points;
}

export function buildBookedRevenueMetrics(rows, { startDate, endDate, period = "last 30 days" } = {}) {
  const normalized = rows.map(normalizeBookingRecord).filter((booking) => booking.included);

  if (normalized.length === 0) {
    return {
      value: "$0.00",
      change: "0.0%",
      trend: "flat",
      subtitle: "vs. $0.00 previous period",
      revenueDailyData: [],
      avgBookingValue: 0,
      avgBookingDeltaText: "0.0%",
      avgBookingDeltaTrend: "flat",
      bookingsCount: 0,
      bookingsCountLabel: "Bookings in Range",
      bookingsCountDeltaText: "+0",
      bookingsCountDeltaTrend: "flat",
      topEventType: "N/A",
      topEventAmount: 0,
      bookingsTableRows: [],
      comparisonRangeLabel: "previous period",
    };
  }

  const currentRange = getRangeDates(startDate, endDate);
  const comparisonRange = getComparisonRange(currentRange.start, currentRange.end, period);
  const isWithinRange = (booking, rangeStart, rangeEnd) => {
    const key = getDateKey(startOfUtcDay(booking.bookedAt));
    return key >= rangeStart && key <= rangeEnd;
  };

  const currentRows = normalized.filter((booking) => isWithinRange(booking, getDateKey(currentRange.start), getDateKey(currentRange.end)));
  const previousRows = normalized.filter((booking) => isWithinRange(booking, comparisonRange.start, comparisonRange.end));

  const currentRevenue = currentRows.reduce((sum, booking) => sum + booking.revenue, 0);
  const previousRevenue = previousRows.reduce((sum, booking) => sum + booking.revenue, 0);
  const currentAvg = currentRows.length > 0 ? currentRevenue / currentRows.length : 0;
  const previousAvg = previousRows.length > 0 ? previousRevenue / previousRows.length : 0;
  const bookingsCountDelta = currentRows.length - previousRows.length;
  const eventTotals = new Map();

  currentRows.forEach((booking) => {
    const key = booking.fh_parent_name || "Unknown";
    eventTotals.set(key, (eventTotals.get(key) || 0) + booking.revenue);
  });

  const [topEventType = "N/A", topEventAmount = 0] =
    Array.from(eventTotals.entries()).sort((a, b) => b[1] - a[1])[0] || [];

  const bookingsTableRows = currentRows
    .slice()
    .sort((a, b) => b.bookedAt - a.bookedAt)
    .map((booking) => ({
      id: String(booking.id || booking.fh_booking_id || ""),
      client: booking.name || booking.email_address || "Unknown",
      date: booking.bookedAt.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }),
      time: booking.bookedAt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
      }),
      event: booking.fh_parent_name || "N/A",
      amount: booking.revenue,
    }));

  return {
    value: formatCurrencyCompact(currentRevenue),
    change: formatChangePercent(currentRevenue, previousRevenue),
    trend: currentRevenue > previousRevenue ? "up" : currentRevenue < previousRevenue ? "down" : "flat",
    subtitle: `vs. ${formatCurrencyCompact(previousRevenue)} ${comparisonRange.label}`,
    revenueDailyData: buildDailyRevenueSeries(currentRows, currentRange.start, currentRange.end),
    avgBookingValue: currentAvg,
    avgBookingDeltaText: formatChangePercent(currentAvg, previousAvg),
    avgBookingDeltaTrend: currentAvg > previousAvg ? "up" : currentAvg < previousAvg ? "down" : "flat",
    bookingsCount: currentRows.length,
    bookingsCountLabel: "Bookings in Range",
    bookingsCountDeltaText: `${bookingsCountDelta >= 0 ? "+" : ""}${bookingsCountDelta}`,
    bookingsCountDeltaTrend: bookingsCountDelta > 0 ? "up" : bookingsCountDelta < 0 ? "down" : "flat",
    topEventType,
    topEventAmount,
    bookingsTableRows,
    comparisonRangeLabel: comparisonRange.label,
  };
}
